// Round 40 — per-user UI preferences that roam across machines.
// Server side: PreferencesController (GET api/preferences,
// PUT api/preferences/{key}). Every call here is fire-and-forget /
// silent-fallback: if the endpoint isn't deployed yet (404) or the network
// hiccups, the app keeps working from localStorage exactly as before.
import api from "../api";

/** Fetch all of the caller's preferences as { key: value }. Returns {} on any failure. */
export async function fetchAllPrefs() {
    try {
        const { data } = await api.get("/preferences");
        return data && typeof data === "object" ? data : {};
    } catch (e) {
        return {};
    }
}

/** Save one preference. Never throws; failures are silently ignored. */
export function savePref(key, value) {
    try {
        api.put(`/preferences/${encodeURIComponent(key)}`, { value: String(value ?? "") })
            .catch(() => { /* endpoint missing or offline — localStorage still has it */ });
    } catch (e) { /* ignore */ }
}

/** Delete one preference (reset to default). Never throws. */
export function deletePref(key) {
    try {
        api.delete(`/preferences/${encodeURIComponent(key)}`)
            .catch(() => { /* ignore */ });
    } catch (e) { /* ignore */ }
}

/**
 * App-start sync: pull server prefs and seed localStorage + the DOM so the
 * rest of the app (which reads localStorage at mount) just works.
 * Server wins over local because it's the user's latest cross-machine state.
 */
export async function syncPrefsAtStartup() {
    const prefs = await fetchAllPrefs();
    try {
        if (prefs.homeLayout) localStorage.setItem("ssw:homeLayout:v1", prefs.homeLayout);
        if (prefs.tableDensity) {
            localStorage.setItem("ssw:tableDensity", prefs.tableDensity);
            document.documentElement.setAttribute("data-table-density", prefs.tableDensity);
        }
        // Round PREF-1 — the user's default THEME roams too. Seed local
        // storage and apply the variant attribute immediately so the very
        // first paint is already themed; the picker (LightDark.js) hears
        // ssw:prefsSynced and aligns the layout mode if needed.
        if (prefs.theme) {
            localStorage.setItem("app.theme", prefs.theme);
            const variant = prefs.theme.split("-")[1];
            if (variant && variant !== "classic") {
                document.documentElement.setAttribute("data-app-theme", variant);
            } else {
                document.documentElement.removeAttribute("data-app-theme");
            }
        }
        // Round PREF-1 — Recently Visited roams across machines as well.
        if (prefs.recents) localStorage.setItem("sswHome:recents", prefs.recents);
    } catch (e) { /* storage blocked — non-fatal */ }
    // Round 40 hotfix1 — announce arrival so already-mounted screens
    // (e.g. Home, which reads localStorage at mount) can re-apply.
    try {
        window.dispatchEvent(new CustomEvent("ssw:prefsSynced", { detail: prefs }));
    } catch (e) { /* older browsers — refresh picks prefs up instead */ }
    return prefs;
}
